// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useState } from 'react';

import { formatDate, useEvolveGet, type WorkOrderPaginated } from '@evolvemep/api-ts';
import { Flex, Stack, Text } from '@mantine/core';

import { getDisplayNameWO } from 'modules/Shop/WorkOrders/WorkOrderDetail/common';
import { WorkOrderActionButtons } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/WorkOrderActionButtons';
import { ActionWorkOrderMenu } from 'modules/Shop/WorkOrders/WorkOrdersPage/ActionWorkOrderMenu';
import { useWorkOrders } from 'modules/Shop/WorkOrders/WorkOrdersPage/useWorkOrders';

type Props = {
  workOrder: WorkOrderPaginated;
  onWorkOrderSelected: (workOrder: WorkOrderPaginated) => void;
  selected: boolean;
  /** Called after this work order is deleted so the parent can clear the selection and refresh the list. */
  onWorkOrderDeleted: () => void;
};

export const WorkOrderSelector = ({
  workOrder: initialWorkOrder,
  onWorkOrderSelected: onWorkOrderSelectedParent,
  selected,
  onWorkOrderDeleted,
}: Props) => {
  const [workOrder, setWorkOrder] = useState(initialWorkOrder);
  const { setWorkOrderAsPending } = useWorkOrders();

  const [loading, setLoading] = useState(false);

  const onWorkOrderUpdated = useCallback(
    (updatedWorkOrder: WorkOrderPaginated) => {
      setWorkOrder(updatedWorkOrder);
      onWorkOrderSelectedParent(updatedWorkOrder);
    },
    [onWorkOrderSelectedParent],
  );

  const { apiCall } = useEvolveGet(`shop/workOrder/${workOrder.workOrderId}`);
  const refreshWorkOrder = useCallback(async () => apiCall().then(onWorkOrderUpdated), [apiCall, onWorkOrderUpdated]);

  const onWorkOrderSelected = useCallback(() => {
    onWorkOrderSelectedParent(workOrder);
    if (workOrder.workOrderStateName === 'Submitted') {
      setLoading(true);
      void setWorkOrderAsPending(workOrder.workOrderId)
        .then(refreshWorkOrder)
        .finally(() => setLoading(false));
    }
  }, [onWorkOrderSelectedParent, refreshWorkOrder, setWorkOrderAsPending, workOrder]);

  return (
    <Flex
      ref={(ref) => {
        if (selected) ref?.scrollIntoView({ behavior: 'smooth' });
      }}
      className="work-order-list-item"
      p="md"
      align="center"
      justify="space-between"
      onClick={onWorkOrderSelected}
      bg={selected ? 'gray.1' : undefined}
      style={{
        cursor: 'pointer',
      }}
      gap="sm"
    >
      <Stack gap={2} style={{ overflow: 'hidden' }}>
        <Text lineClamp={2}>{getDisplayNameWO(workOrder)}</Text>
        <Text c="dimmed" fz="sm">
          Need by: {formatDate(workOrder.needByDate, 'MM/dd/yyyy')}
        </Text>
      </Stack>
      <Flex flex="0 0 auto" gap="xs" align="center">
        <WorkOrderActionButtons
          onWorkOrderUpdated={onWorkOrderUpdated}
          workOrder={workOrder}
          inline
          onWorkOrderActioned={onWorkOrderSelected}
        />
        <ActionWorkOrderMenu
          workOrder={workOrder}
          onWorkOrderActioned={refreshWorkOrder}
          onWorkOrderDeleted={onWorkOrderDeleted}
          loading={loading}
          isWorkOrdersPage={false}
        />
      </Flex>
    </Flex>
  );
};
